/*
 * THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.
 *
 * This file has been auto-generated. Any modifications
 * you make will be overwritten when the file is regenerated.
 *
 * For more information or questions, please go to #design-eng
 *
 */

/*
 * Base 2.0 Elevation Tokens
 * =========================
 *
 * Each hierarchy ships as a single, ready-to-drop `box-shadow` string composed
 * from the underlying per-property CSS custom properties (see
 * base-elevation-v2.css). Each composite paints the key shadow on top of the
 * ambient shadow per spec.
 *
 *   Hierarchy:  lowest, low, mid, high, highest
 *   Naming:     elevation{Hierarchy}        (e.g. elevationMid)
 *
 * Resolved values flow through the CSS layer and respond to data attributes
 * (data-base-version-color, data-theme, data-elevated) without any JS theme
 * swap. Advanced consumers can still address the underlying x/y/blur/spread/
 * color tokens directly via the
 * `--base-elevation-{hierarchy}-{layer}-{property}` custom properties — for
 * example to override a single facet or to drive a transition.
 *
 * Caveats:
 *   • The Figma source specifies luminosity blending per shadow layer.
 *     CSS box-shadow has no per-layer blend-mode primitive, so blending is
 *     intentionally not encoded.
 *   • Shadow colors are inlined as rgba() in the CSS layer rather than
 *     referencing the neutral primitive token, because Figma stores them
 *     as raw RGBA values, not aliases.
 */

/*
 * Composite box-shadow values for the five v2 elevation hierarchies.
 *
 * Each value is a complete `box-shadow` declaration value, e.g.:
 *
 *   <Surface css={{ boxShadow: elevationTokensV2CssVars.elevationMid }} />
 *
 * Control the active variant purely via HTML data attributes on any ancestor
 * element — no JS theme swap is required:
 *
 *   data-base-version-color="2"   — opt in to v2 tokens (base-elevation-v2.css required)
 *   data-theme="dark"             — dark mode
 *   data-elevated="true"          — dark-elevated surface
 */
export const elevationTokensV2CssVars = ({
  elevationLowest: 'var(--base-elevation-lowest-key-x) var(--base-elevation-lowest-key-y) var(--base-elevation-lowest-key-blur) var(--base-elevation-lowest-key-spread) var(--base-elevation-lowest-key-color), var(--base-elevation-lowest-ambient-x) var(--base-elevation-lowest-ambient-y) var(--base-elevation-lowest-ambient-blur) var(--base-elevation-lowest-ambient-spread) var(--base-elevation-lowest-ambient-color)',
  elevationLow: 'var(--base-elevation-low-key-x) var(--base-elevation-low-key-y) var(--base-elevation-low-key-blur) var(--base-elevation-low-key-spread) var(--base-elevation-low-key-color), var(--base-elevation-low-ambient-x) var(--base-elevation-low-ambient-y) var(--base-elevation-low-ambient-blur) var(--base-elevation-low-ambient-spread) var(--base-elevation-low-ambient-color)',
  elevationMid: 'var(--base-elevation-mid-key-x) var(--base-elevation-mid-key-y) var(--base-elevation-mid-key-blur) var(--base-elevation-mid-key-spread) var(--base-elevation-mid-key-color), var(--base-elevation-mid-ambient-x) var(--base-elevation-mid-ambient-y) var(--base-elevation-mid-ambient-blur) var(--base-elevation-mid-ambient-spread) var(--base-elevation-mid-ambient-color)',
  elevationHigh: 'var(--base-elevation-high-key-x) var(--base-elevation-high-key-y) var(--base-elevation-high-key-blur) var(--base-elevation-high-key-spread) var(--base-elevation-high-key-color), var(--base-elevation-high-ambient-x) var(--base-elevation-high-ambient-y) var(--base-elevation-high-ambient-blur) var(--base-elevation-high-ambient-spread) var(--base-elevation-high-ambient-color)',
  elevationHighest: 'var(--base-elevation-highest-key-x) var(--base-elevation-highest-key-y) var(--base-elevation-highest-key-blur) var(--base-elevation-highest-key-spread) var(--base-elevation-highest-key-color), var(--base-elevation-highest-ambient-x) var(--base-elevation-highest-ambient-y) var(--base-elevation-highest-ambient-blur) var(--base-elevation-highest-ambient-spread) var(--base-elevation-highest-ambient-color)'
} as const);
export type ElevationTokensV2CssVarsType = typeof elevationTokensV2CssVars;

/*
 * JS-driven (literal) composite box-shadow values, one set per mode.
 *
 * These mirror `elevationTokensV2CssVars` key-for-key, but inline the actual
 * x/y/blur/spread/color values instead of `var(--base-elevation-*)`
 * references. Use them when you need a self-contained box-shadow that does not
 * depend on `base-elevation-v2.css` being on the page or on any
 * `data-base-version-*` / `data-theme` / `data-elevated` attribute — e.g.
 * raw inline styles, non-DOM renderers, tests, or a JS theme swap that selects
 * the mode at render time (the elevation analogue of the Color 2.0 JS path).
 *
 *   elevationTokensV2Light        — light mode
 *   elevationTokensV2Dark         — dark mode
 *   elevationTokensV2DarkElevated — dark-elevated surface
 *
 * The same caveats as the CSS layer apply: per-layer luminosity blending is
 * not encoded, and shadow colors are inlined as rgba().
 */
export const elevationTokensV2Light = ({
  elevationLowest: '0 0 2px 0 rgba(0, 0, 0, 0.05), 0 0.5px 10px -1px rgba(107, 107, 107, 0.17)',
  elevationLow: '0 0.25px 3.5px 0 rgba(0, 0, 0, 0.07), 0 0.75px 16px -2px rgba(107, 107, 107, 0.2)',
  elevationMid: '0 0.75px 6px 0 rgba(0, 0, 0, 0.07), 0 1.5px 28px -3px rgba(107, 107, 107, 0.24)',
  elevationHigh: '0 1.25px 10px 0 rgba(0, 0, 0, 0.07), 0 2.25px 40px -6px rgba(107, 107, 107, 0.32)',
  elevationHighest: '0 1.75px 16px 0 rgba(0, 0, 0, 0.07), 0 2.75px 42px -7px rgba(107, 107, 107, 0.37)'
} as const);
export const elevationTokensV2Dark = ({
  elevationLowest: '0 -0.5px 1px 0 rgba(224, 224, 224, 0.1), 0 2px 8px -1px rgba(10, 10, 10, 0.48)',
  elevationLow: '0 -0.5px 1.5px 0 rgba(224, 224, 224, 0.12), 0 4px 12px -2px rgba(10, 10, 10, 0.56)',
  elevationMid: '0 -1px 2px 0 rgba(224, 224, 224, 0.16), 0 6px 18px -3px rgba(10, 10, 10, 0.64)',
  elevationHigh: '0 -1px 2px 0 rgba(224, 224, 224, 0.18), 0 8px 28px -4px rgba(10, 10, 10, 0.72)',
  elevationHighest: '0 -1.5px 3px 0 rgba(224, 224, 224, 0.2), 0 8px 32px -6px rgba(10, 10, 10, 0.8)'
} as const);
export const elevationTokensV2DarkElevated = ({
  elevationLowest: '0 -0.5px 1px 0 rgba(224, 224, 224, 0.1), 0 2px 8px -1px rgba(10, 10, 10, 0.48)',
  elevationLow: '0 -0.5px 1.5px 0 rgba(224, 224, 224, 0.12), 0 4px 12px -2px rgba(10, 10, 10, 0.56)',
  elevationMid: '0 -1px 2px 0 rgba(224, 224, 224, 0.16), 0 6px 18px -3px rgba(10, 10, 10, 0.64)',
  elevationHigh: '0 -1px 2px 0 rgba(224, 224, 224, 0.18), 0 8px 28px -4px rgba(10, 10, 10, 0.72)',
  elevationHighest: '0 -1.5px 3px 0 rgba(224, 224, 224, 0.2), 0 8px 32px -6px rgba(10, 10, 10, 0.8)'
} as const);

/** Shape shared by every per-mode literal elevation token set. */
export type ElevationTokensV2Type = Record<keyof ElevationTokensV2CssVarsType, string>;